import React from "react";
import { Box, Typography, Skeleton } from "@mui/material";
import { Badge } from "@vilocnv/allsetra-core";
import { HiddenBadgeIcon } from "components/ui/icons";

export const ADDRESS_TOLERANCE = 1e-6;

export interface AddressInfo {
  shouldShow: boolean;
  isHidden: boolean;
  isUnknown: boolean;
}

export const getAddressInfo = (
  lat: number | null | undefined,
  lon: number | null | undefined,
  rideMode: number
): AddressInfo => {
  // null/undefined coordinates are privacy-redacted
  if (lat === null || lon === null || lat === undefined || lon === undefined) {
    return {
      shouldShow: false,
      isHidden: true,
      isUnknown: false
    };
  }

  const hasCoordinates = lat !== 0 && lon !== 0;

  if (hasCoordinates) {
    return {
      shouldShow: true,
      isHidden: false,
      isUnknown: false
    };
  }

  // Private or Mixed rides with zero coords: treat as hidden (defensive)
  if (rideMode === 2 || rideMode === 4) {
    return {
      shouldShow: false,
      isHidden: true,
      isUnknown: false
    };
  }

  // Zero coordinates mean location data is unavailable
  return {
    shouldShow: false,
    isHidden: false,
    isUnknown: true
  };
};

interface AddressDisplayProps {
  address?: string;
  addressInfo: AddressInfo;
  isLoading?: boolean;
  userCanViewAddress?: boolean;
  rideMode?: number;
  translator?: any;
}

export const AddressDisplay: React.FC<AddressDisplayProps> = ({
  address,
  addressInfo,
  isLoading = false,
  userCanViewAddress = true,
  rideMode,
  translator: t
}) => {
  // Check if user can view address for this ride (role-based)
  const canView = userCanViewAddress;

  // For Private/Mixed rides where user cannot view address, always show hidden
  if (!canView && (rideMode === 2 || rideMode === 4)) {
    return (
      <Box sx={{ alignSelf: "flex-start" }}>
        <Badge
          colorScheme="blank"
          icon={<HiddenBadgeIcon />}
          textWeight="light"
        >
          {t ? t("typography.addressIsHidden") : "Address is hidden"}
        </Badge>
      </Box>
    );
  }

  if (addressInfo.shouldShow) {
    return isLoading ? (
      <Skeleton variant="text" width={180} height={20} />
    ) : (
      <Typography variant="body2">{address || "-"}</Typography>
    );
  }

  if (addressInfo.isHidden) {
    return (
      <Box sx={{ alignSelf: "flex-start" }}>
        <Badge
          colorScheme="blank"
          icon={<HiddenBadgeIcon />}
          textWeight="light"
        >
          {t ? t("typography.addressIsHidden") : "Address is hidden"}
        </Badge>
      </Box>
    );
  }

  // Unknown
  return (
    <Box sx={{ alignSelf: "flex-start" }}>
      <Badge colorScheme="blank" textWeight="light">
        {t ? t("typography.addressUnknown") : "Address unknown"}
      </Badge>
    </Box>
  );
};
